import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import { Box, Card, CardContent, Typography } from '@mui/material';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import { formatTooltipMoney } from '../../../../helpers/formatMoneyHelper';

const COLORS = [
  'rgb(203,100,35)', // Apex
  '#111827', // Momentum
  '#555B63', // Prescient
  '#A6ABB2', // BCI
  '#C9CDD2',
  '#E2E5E9',
  '#BDBDBD',
  '#D6A77A'
];

export default function ProviderPerformanceChart({ data }) {
  const sortedData = useMemo(() => {
    return [...(data || [])].sort((a, b) => new Date(a.asof) - new Date(b.asof));
  }, [data]);

  const chartData = useMemo(() => {
    return sortedData.map((item) => {
      const row = {
        asof: item.asof,
        label: dayjs(item.asof).format('D MMM')
      };

      (item.providers || []).forEach((provider) => {
        row[provider.name] = provider.amount;
      });

      return row;
    });
  }, [sortedData]);

  // const providers = useMemo(() => {
  //   const latest = sortedData.at(-1);

  //   if (!latest) return [];

  //   return latest.providers.map((provider, index) => ({
  //     ...provider,
  //     color: COLORS[index % COLORS.length]
  //   }));
  // }, [sortedData]);

  //Test provider selection
  const providers = useMemo(() => {
    const itemWithMostProviders = sortedData.reduce(
      (max, item) =>
        item.providers.length > max.providers.length ? item : max,
      sortedData[0]
    );

    if (!itemWithMostProviders) return [];

    return itemWithMostProviders.providers.map((provider, index) => ({
      ...provider,
      color: COLORS[index % COLORS.length]
    }));
  }, [sortedData]);

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #ECECEC',
        boxShadow: '0 2px 12px rgba(0,0,0,.05)'
      }}>
      <CardContent
        sx={{
          p: 2,
          '&:last-child': {
            pb: 2
          }
        }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            mb: 3
          }}>
          Total Provider Level AUM
        </Typography>

        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 15,
              left: 5,
              bottom: 10
            }}>
            <defs>
              {providers.map((provider) => (
                <linearGradient
                  key={provider.id}
                  id={`gradient-${provider.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop offset="0%" stopColor={provider.color} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={provider.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} stroke="#F4F4F4" strokeDasharray="0" />

            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              interval={0}
              tickMargin={10}
              tick={{
                fill: '#666',
                fontSize: 12
              }}
              tickFormatter={(value, index) => {
                const date = dayjs(chartData[index]?.asof);

                const totalDays =
                  chartData.length > 1
                    ? dayjs(chartData.at(-1).asof).diff(dayjs(chartData[0].asof), 'day')
                    : 0;

                if (totalDays <= 10) {
                  return value;
                }

                if (totalDays <= 45) {
                  return index % 7 === 0 ? value : '';
                }

                return date.date() === 1 ? date.format('MMM') : '';
              }}
            />

            <YAxis
              tickFormatter={(value) => `${(value / 1000000000).toFixed(1)}B`}
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              tick={{
                fill: '#666',
                fontSize: 14
              }}
            />

            <Tooltip
              cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 1
              }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;

                const point = payload[0].payload;

                return (
                  <Box
                    sx={{
                      bgcolor: '#fff',
                      boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                      p: 1.5,
                      border: '1px solid #ECECEC',
                      minWidth: 190
                    }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#666',
                        mb: 1
                      }}>
                      {dayjs(point.asof).format('DD MMM YYYY')}
                    </Typography>

                    {payload.map((entry) => (
                      <Box
                        key={entry.dataKey}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.75}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: entry.color
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize: 12
                            }}>
                            {entry.dataKey}
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            ml: 2
                          }}>
                          {formatTooltipMoney(entry.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              }}
            />

            {providers.map((provider) => (
              <Area
                key={provider.id}
                type="monotone"
                dataKey={provider.name}
                stroke={provider.color}
                strokeWidth={2}
                fill={`url(#gradient-${provider.id})`}
                fillOpacity={1}
                dot={{
                  r: 1,
                  fill: provider.color,
                  stroke: provider.color,
                  strokeWidth: 1
                }}
                activeDot={{
                  r: 3,
                  fill: provider.color,
                  stroke: '#fff',
                  strokeWidth: 2
                }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>

        {/* Legend */}
        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} mt={3}>
          {providers.map((provider) => (
            <Box key={provider.id} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: provider.color
                }}
              />

              <Typography
                sx={{
                  fontSize: 12,
                  color: '#555'
                }}>
                {provider.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
